import React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Database, Copy, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface DataModelDialogProps {
  sql: string;
}

export const DataModelDialog = ({ sql }: DataModelDialogProps) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const handleCopy = () => {
    navigator.clipboard.writeText(sql);
    toast({ title: "SQL copied to clipboard" });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Database className="h-4 w-4" />
          Apply Data Model
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Database Schema SQL</DialogTitle>
          <DialogDescription>
            Copy this SQL and run it in your Supabase SQL editor to set up your database schema.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Textarea
            value={sql}
            readOnly
            className="font-mono text-sm min-h-[300px] bg-muted"
          />

          <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
            <h4 className="font-semibold mb-2 text-sm">How to apply:</h4>
            <ol className="text-sm space-y-2 list-decimal list-inside text-muted-foreground">
              <li>Copy the SQL above using the button below</li>
              <li>Open your backend dashboard</li>
              <li>Navigate to Database → SQL Editor</li>
              <li>Paste and run the SQL</li>
              <li>Verify tables are created successfully</li>
            </ol>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button onClick={handleCopy} className="gap-2">
            <Copy className="h-4 w-4" />
            Copy SQL
          </Button>
          <Button variant="secondary" className="gap-2" asChild>
            <a href="https://dayone.app/docs" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
              View Docs
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
